import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import Button from '@mui/material/Button'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'

import DateRangePicker from '../../../../../components/DateRangePicker'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../../../components/SelectSearch/ChannelSelectSearch'
import ProductGroupSelectSearch from '../../../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import useAxios from '../../../../../hooks/useAxios'
import { IMerchant } from '../../../../../types/merchant'
import { AllowShippingProviderLists } from '../../../../../utils/provider'

export interface IReturnFilterMapped {
  categoryObjectIds?: string[]
  distributionObjectIds?: string[]
  productObjectIds?: string[]
  productGroups?: string[]
  trackingProvider?: string
  dateType?: string
  startDate?: string
  endDate?: string
}

interface FilterGroupProps {
  filter: IReturnFilterMapped
  onFilterChange: (filter: IReturnFilterMapped) => void
  onSearch: (filter: IReturnFilterMapped) => void
}

const menuItemsDateType = [
  {
    label: 'วันที่ขนส่งตีกลับ',
    value: 'returnAt',
  },
  {
    label: 'วันที่ร้านรับของเข้า',
    value: 'returnedAt',
  },
]

const DailyReturnReportFilter = (props: FilterGroupProps) => {
  const { filter, onFilterChange, onSearch } = props
  const {
    startDate,
    endDate,
    productObjectIds,
    distributionObjectIds,
    categoryObjectIds,
    trackingProvider = 'all',
    dateType = 'returnAt',
  } = filter
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  const merchant = useAxios<IMerchant>({
    method: 'GET',
    url: '/merchant/me',
  })

  const providers = merchant.data
    ? AllowShippingProviderLists(merchant.data)
    : []

  const handleFilterChange = useCallback(
    (newFilter: Partial<IReturnFilterMapped>) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            onFilterChange({ ...filter, startDate, endDate })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormControl fullWidth>
          <InputLabel id="return-report-date-type">ประเภทวันที่</InputLabel>
          <Select
            labelId="return-report-date-type"
            value={dateType}
            label="ประเภทวันที่"
            onChange={(e) =>
              handleFilterChange({ dateType: String(e.target.value) })
            }
          >
            {menuItemsDateType.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormControl fullWidth>
          <InputLabel id="return-report-provider">ขนส่ง</InputLabel>
          <Select
            labelId="return-report-provider"
            value={trackingProvider}
            label="ขนส่ง"
            onChange={(e) =>
              handleFilterChange({ trackingProvider: String(e.target.value) })
            }
          >
            <MenuItem value="all">เลือกทั้งหมด</MenuItem>
            {providers
              .filter((item) => item.value !== 'all')
              .map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={2}>
        <ProductSelectSearch
          resetAutoComplete={resetAutoComplete}
          products={productObjectIds}
          handleFilterChange={handleFilterChange}
          onSelect={(e) => {
            const selectedProductIds = e?.map(
              (product: any) => product?.value?.objectId,
            )
            handleFilterChange({ productObjectIds: selectedProductIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <ProductGroupSelectSearch
          resetAutoComplete={resetAutoComplete}
          productGroups={filter.productGroups}
          onSelect={(e) => {
            const selectedGroupIds = e?.map((group) => group.value.objectId)
            handleFilterChange({ productGroups: selectedGroupIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <CategorySelectSearch
          resetAutoComplete={resetAutoComplete}
          categories={categoryObjectIds ?? []}
          onSelect={(e) => {
            const selectedCategoryIds = e?.map(
              (category: any) => category?.value?.objectId,
            )
            handleFilterChange({ categoryObjectIds: selectedCategoryIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={2}>
        <ChannelSelectSearch
          resetAutoComplete={resetAutoComplete}
          selected={distributionObjectIds ?? []}
          label={'เลือกช่องทางการขาย'}
          onSelect={(e) => {
            const selectedChannelIds = e?.map(
              (channel: any) => channel?.value?.objectId,
            )
            handleFilterChange({ distributionObjectIds: selectedChannelIds })
          }}
        />
      </Grid>
      <Grid item xs={8} lg={2}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => {
            const defaultFilter: IReturnFilterMapped = {
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              trackingProvider: 'all',
              dateType: 'returnAt',
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            }
            onFilterChange(defaultFilter)
            onSearch(defaultFilter)
            setResetAutoComplete(new Date().getTime().toString())
          }}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
    </Grid>
  )
}

export default DailyReturnReportFilter
